import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useCallback, useEffect, useState } from 'react';
import { Slider } from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import { ToggleItemVisibility } from '../ToggleItemVisibility';
import { useKey } from '@/hooks';
import queryString from 'query-string';
import { updateUrl } from '@/utils';

export const MarkerLabelControls = () => {
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const [showPointOfInterestMarkerLabels, setShowPointOfInterestMarkerLabels] = useAtom(
    state.showPointOfInterestMarkerLabels
  );
  const [markerLabelScale, setMarkerLabelScale] = useAtom<number>(state.markerLabelScale);
  const [swivelMarkerLabels, setSwivelMarkerLabels] = useAtom<boolean>(state.swivelMarkerLabels);
  const [markerLabelHeightVariance, setMarkerLabelHeightVariance] = useAtom<number>(
    state.markerLabelHeightVariance
  );
  const [unfinishedScaleValue, setUnfinishedScaleValue] = useState(markerLabelScale);
  const [unfinishedHeightVarianceValue, setUnfinishedHeightVarianceValue] =
    useState(markerLabelHeightVariance);

  useEffect(() => {
    const query = window.location.search;
    const parsed = queryString.parse(query, { arrayFormat: 'comma' });
    if (parsed.annotationHeight) {
      const annotationHeight = JSON.parse(parsed.annotationHeight.toString())
      if (selectedSpherical) {
        setSwivelMarkerLabels(false);
        setUnfinishedHeightVarianceValue(0);
      } else {
        setUnfinishedHeightVarianceValue(annotationHeight);
      }
    }
  }, [selectedSpherical, setSwivelMarkerLabels, setMarkerLabelHeightVariance]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setMarkerLabelScale(unfinishedScaleValue);
    }, 250);

    return () => clearTimeout(debounce);
  }, [setMarkerLabelScale, unfinishedScaleValue]);

  const handleScaleValueChanged = (event: Event, value: number | number[]) => {
    setUnfinishedScaleValue(value as number);
  };

  const handleHeightVarianceValueChanged = (event: Event, value: number | number[]) => {
    setUnfinishedHeightVarianceValue(value as number);
    updateUrl('annotationHeight', String(value))

  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      setMarkerLabelHeightVariance(unfinishedHeightVarianceValue);
    }, 250);

    return () => clearTimeout(debounce);
  }, [setMarkerLabelHeightVariance, unfinishedHeightVarianceValue]);

  const toggleSwivelMarkerLabels = () => {
    setSwivelMarkerLabels((prev) => !prev);
  };

  const togglePointOfInterestMarkerLabels = useCallback(() => {
    setShowPointOfInterestMarkerLabels((current) => !current);
  }, [setShowPointOfInterestMarkerLabels]);

  useKey('l', togglePointOfInterestMarkerLabels, 'alt');
  useKey('o', toggleSwivelMarkerLabels, 'alt');

  return (
    <>
      <ToggleItemVisibility
        label="Show all annotations"
        isVisible={showPointOfInterestMarkerLabels}
        toggleVisibility={togglePointOfInterestMarkerLabels}
        tooltipTitle="'Press Alt + L'"
      />
      {showPointOfInterestMarkerLabels && (
        <>
          <div>
            <FormLabel>Annotation Scale</FormLabel>
            <Slider
              min={0.5}
              step={0.5}
              max={10}
              aria-label="Annotation Scale"
              getAriaValueText={(value) => `${value}`}
              valueLabelDisplay="auto"
              value={unfinishedScaleValue}
              onChange={handleScaleValueChanged}
              size="small"
            />
          </div>
          <div>
            <FormLabel>Annotation Height Variance</FormLabel>
            <Slider
              min={0}
              step={1}
              max={10}
              aria-label="Annotation Height Variance"
              getAriaValueText={(value) => `${value}`}
              valueLabelDisplay="auto"
              value={unfinishedHeightVarianceValue}
              onChange={handleHeightVarianceValueChanged}
              size="small"
            />
          </div>
          <ToggleItemVisibility
            label="Switch Annotation Orientation"
            isVisible={swivelMarkerLabels}
            toggleVisibility={toggleSwivelMarkerLabels}
            tooltipTitle="'Press Alt + O'"
          />
        </>
      )}
    </>
  );
};
